import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import CadastroEmailsClienteModal from '../components/CadastroEmailsClienteModal'
import { useIbsCbsDescricoes } from '../hooks/useIbsCbsDescricoes'
import { useEmpresa } from '../contexts/EmpresaContext'

interface NFSeRow {
  empcod:       number
  serie:        string
  numero:       number
  cnpj_tomador: string
  nome_tomador?: string
  desc_serv:    string
  data_compet:  string
  valor_total:  string
  sitnfe:       number
  chave_acesso: string
  xmotivo:      string
  tp_amb:       string
  data_emissao: string
  vl_iss:       string
  pe_iss:       string
  vl_inss:      string
  vl_pis:       string
  vl_cof:       string
  vl_ir:        string
  vl_csll:      string
  nnfse:        string
  cstat:        string
  xmlpro:       string
  xmlnfe:       string
  numero_viagem?: number | null
  natureza_cod?: number | null
  natureza_det?: string | null
}

interface Filtros {
  tomador:    string
  numero:     string
  dataInicio: string
  dataFim:    string
  sitnfe:     string
}

const SIT: Record<number, { label: string; color: string; bg: string }> = {
  5: { label: 'Pendente',   color: '#b45309', bg: '#fef3c7' },
  6: { label: 'Autorizada', color: '#15803d', bg: '#dcfce7' },
  4: { label: 'Rejeitada',  color: '#dc2626', bg: '#fee2e2' },
  7: { label: 'Cancelada',  color: '#dc2626', bg: '#fee2e2' },
}

function fmtDateInput(d: Date) { return d.toISOString().slice(0, 10) }
function fmtData(iso: string | null) {
  if (!iso) return '-'
  return iso.slice(0, 10).split('-').reverse().join('/')
}
function fmtMoeda(v: string | number) {
  const n = typeof v === 'string' ? parseFloat(v) : v
  if (isNaN(n)) return '—'
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function fmtDoc(doc: string) {
  const n = (doc ?? '').replace(/\D/g, '')
  if (n.length === 14) return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  if (n.length === 11) return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  return doc
}
function filtroPadrao(): Filtros {
  const hoje  = new Date()
  const inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1)
  return { tomador: '', numero: '', dataInicio: fmtDateInput(inicio), dataFim: fmtDateInput(hoje), sitnfe: '' }
}

const inp: React.CSSProperties = {
  border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
  fontSize: '0.82rem', height: 30, background: '#fff',
}

function kv(label: string, value: React.ReactNode) {
  return (
    <div>
      <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' as const, marginBottom: 1 }}>{label}</div>
      <div style={{ fontSize: '0.82rem', color: '#374151' }}>{value}</div>
    </div>
  )
}

// Mesmo destaque âmbar do CT-e OS pra sinalizar vínculo com ficha do módulo Turismo.
function kvViagem(value: React.ReactNode) {
  return (
    <div>
      <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#b45309', textTransform: 'uppercase' as const, marginBottom: 1 }}>Nº da Viagem</div>
      <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#92400e' }}>{value ?? '—'}</div>
    </div>
  )
}

interface PessoaNfse { nome: string; doc: string; im: string; fone: string; email: string; endereco: string }
interface NfseDetalhe {
  numero: string; codVerificacao: string; competencia: string; dataEmissao: string
  localPrestacao: string
  prestador: PessoaNfse; tomador: PessoaNfse
  cServ: string; xServ: string; qTrib: string; vUnit: string; uTrib: string
  baseCalculo: string; aliquotaIss: string
}

const pessoaVazia: PessoaNfse = { nome: '', doc: '', im: '', fone: '', email: '', endereco: '' }

function txt(el: Element | Document | null, tag: string): string {
  return el?.getElementsByTagName(tag)[0]?.textContent?.trim() ?? ''
}

// Notas emitidas por aqui usam o schema nacional (<infNFSe>); notas antigas do ERP (pré-web app)
// usam o padrão ABRASF (<InfNfse>/<CompNfse>) — os nomes de tag são completamente diferentes.
function parseNfse(xml: string): NfseDetalhe | null {
  if (!xml || !xml.trim()) return null
  let doc: Document
  try {
    doc = new DOMParser().parseFromString(xml, 'text/xml')
    if (doc.getElementsByTagName('parsererror').length) return null
  } catch { return null }

  const abrasf = doc.getElementsByTagName('InfNfse')[0]
  if (abrasf) {
    const pessoaAbrasf = (el: Element | null): PessoaNfse => {
      if (!el) return pessoaVazia
      const end = el.getElementsByTagName('Endereco')[0] ?? null
      const linha1 = end ? [txt(end, 'Endereco'), txt(end, 'Numero')].filter(Boolean).join(', ') + (txt(end, 'Complemento') ? ` - ${txt(end, 'Complemento')}` : '') : ''
      const uf = txt(end, 'Uf'), cep = txt(end, 'Cep'), bairro = txt(end, 'Bairro')
      const linha2 = [bairro, uf ? `UF ${uf}` : ''].filter(Boolean).join(' - ')
      return {
        nome: txt(el, 'RazaoSocial'),
        doc:  txt(el, 'Cnpj') || txt(el, 'Cpf'),
        im:   txt(el, 'InscricaoMunicipal'),
        fone: txt(el, 'Telefone'),
        email: txt(el, 'Email'),
        endereco: [linha1, linha2, cep ? `CEP ${cep}` : ''].filter(Boolean).join(' · '),
      }
    }
    const servico = doc.getElementsByTagName('Servico')[0] ?? null
    return {
      numero: txt(abrasf, 'Numero'),
      codVerificacao: txt(abrasf, 'CodigoVerificacao'),
      competencia: txt(abrasf, 'Competencia'),
      dataEmissao: txt(abrasf, 'DataEmissao'),
      localPrestacao: txt(servico, 'CodigoMunicipio'),
      prestador: pessoaAbrasf(doc.getElementsByTagName('PrestadorServico')[0] ?? null),
      tomador:   pessoaAbrasf(doc.getElementsByTagName('TomadorServico')[0] ?? null),
      cServ: txt(servico, 'ItemListaServico') || txt(servico, 'CodigoTributacaoMunicipio'),
      xServ: txt(servico, 'Discriminacao'),
      qTrib: '', vUnit: '', uTrib: '',
      baseCalculo: txt(servico, 'BaseCalculo'),
      aliquotaIss: txt(servico, 'Aliquota'),
    }
  }

  const nacional = doc.getElementsByTagName('infNFSe')[0]
  if (nacional) {
    const pessoaNacional = (el: Element | null): PessoaNfse => {
      if (!el) return pessoaVazia
      const end = el.getElementsByTagName('end')[0] ?? el.getElementsByTagName('ender')[0] ?? null
      const xLgr = txt(end, 'xLgr'), nro = txt(end, 'nro'), xCpl = txt(end, 'xCpl')
      const linha1 = [xLgr, nro].filter(Boolean).join(', ') + (xCpl ? ` - ${xCpl}` : '')
      const xBairro = txt(end, 'xBairro'), xMun = txt(end, 'xMun'), uf = txt(end, 'UF'), cep = txt(end, 'CEP')
      const linha2 = [xBairro, xMun && uf ? `${xMun}/${uf}` : (xMun || uf)].filter(Boolean).join(' - ')
      return {
        nome: txt(el, 'xNome'),
        doc:  txt(el, 'CNPJ') || txt(el, 'CPF'),
        im:   txt(el, 'IM'),
        fone: txt(el, 'fone'),
        email: txt(el, 'xEmail') || txt(el, 'email'),
        endereco: [linha1, linha2, cep ? `CEP ${cep}` : ''].filter(Boolean).join(' · '),
      }
    }
    const serv = doc.getElementsByTagName('serv')[0] ?? null
    const id   = doc.getElementsByTagName('Id')[0] ?? null
    const toms = doc.getElementsByTagName('TomS')[0] ?? doc.getElementsByTagName('TomN')[0] ?? doc.getElementsByTagName('TomE')[0] ?? doc.getElementsByTagName('TomInt')[0] ?? null
    return {
      numero: txt(id, 'nNFS-e'),
      codVerificacao: txt(id, 'cNFS-e'),
      competencia: txt(id, 'dCompet'),
      dataEmissao: [txt(id, 'dEmi'), txt(id, 'hEmi')].filter(Boolean).join(' '),
      localPrestacao: txt(id, 'cLocPrestacao'),
      prestador: pessoaNacional(doc.getElementsByTagName('prest')[0] ?? null),
      tomador:   pessoaNacional(toms),
      cServ: txt(serv, 'cServ'),
      xServ: txt(serv, 'xServ'),
      qTrib: txt(serv, 'qTrib'),
      vUnit: txt(serv, 'vUnit'),
      uTrib: txt(serv, 'uTrib'),
      baseCalculo: '', aliquotaIss: '',
    }
  }

  return null
}

export default function ListaNFSeInfisc() {
  const { empresaCod } = useEmpresa()
  const navigate = useNavigate()
  const { cstDes, classTribDes } = useIbsCbsDescricoes()
  const [lista, setLista]           = useState<NFSeRow[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro]             = useState<string | null>(null)
  const [filtros, setFiltros]       = useState<Filtros>(filtroPadrao)
  const [ativos, setAtivos]         = useState<Filtros>(filtroPadrao)

  // Cancelamento
  const [cancelando, setCancelando]   = useState(false)
  const [rowCancel, setRowCancel]     = useState<NFSeRow | null>(null)
  const [motivo, setMotivo]           = useState('1')
  const [erroCancel, setErroCancel]  = useState<string | null>(null)

  // Consulta
  const [consultaRow, setConsultaRow] = useState<NFSeRow | null>(null)
  const [chaveCopiadaId, setChaveCopiadaId] = useState<number | null>(null)
  const [consultaFinanceiro, setConsultaFinanceiro] = useState<{ numero: number; vencimento: string; valor: number; saldo: number; contaCod: string }[]>([])
  const [consultaTributos, setConsultaTributos] = useState<any[]>([])

  const [acoesMenuId, setAcoesMenuId] = useState<number | null>(null)
  const [acoesMenuPos, setAcoesMenuPos] = useState<{ top: number; left: number } | null>(null)

  // Envio de e-mail
  const [emailModal, setEmailModal] = useState<NFSeRow | null>(null)
  const [emailCarregando, setEmailCarregando] = useState(false)
  const [emailEnviando, setEmailEnviando] = useState(false)
  const [emailErro, setEmailErro] = useState<string | null>(null)
  const [emailMsg, setEmailMsg] = useState<{ texto: string; ok: boolean } | null>(null)
  const [emailRemetenteNome, setEmailRemetenteNome] = useState('')
  const [emailRemetenteEmail, setEmailRemetenteEmail] = useState('')
  const [emailDestNome, setEmailDestNome] = useState('')
  const [emailDestCnpj, setEmailDestCnpj] = useState('')
  const [emailDestinatario, setEmailDestinatario] = useState('')
  const [emailCopias, setEmailCopias] = useState<string[]>([])
  const [cadEmailsAberto, setCadEmailsAberto] = useState(false)
  const [emailAssunto, setEmailAssunto] = useState('')
  const [emailCorpo, setEmailCorpo] = useState('')

  // Transmitir
  const [transmitindoId, setTransmitindoId] = useState<number | null>(null)
  const [transmitirMsg, setTransmitirMsg]   = useState<{ numero: number; ok: boolean; msg: string } | null>(null)

  // Financeiro
  const [finRow, setFinRow]                     = useState<NFSeRow | null>(null)
  const [finQtdParcelas, setFinQtdParcelas]     = useState(1)
  const [finIntervalo, setFinIntervalo]         = useState(30)
  const [finParcelas, setFinParcelas]           = useState<{ numero: number; vencimento: string; valor: number }[]>([])
  const [finContaCod, setFinContaCod]           = useState('')
  const [finContaNome, setFinContaNome]         = useState('')
  const [finContaResultados, setFinContaResultados] = useState<any[]>([])
  const [finContaAberto, setFinContaAberto]     = useState(false)
  const [finSalvando, setFinSalvando]           = useState(false)
  const [finErro, setFinErro]                   = useState<string | null>(null)
  const [finSucesso, setFinSucesso]             = useState(false)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      const params: Record<string, string> = { empcod: String(empresaCod ?? 1) }
      if (ativos.tomador)    params.tomador    = ativos.tomador
      if (ativos.numero)     params.numero     = ativos.numero
      if (ativos.dataInicio) params.dataInicio = ativos.dataInicio
      if (ativos.dataFim)    params.dataFim    = ativos.dataFim
      if (ativos.sitnfe)     params.sitnfe     = ativos.sitnfe
      const r = await api.get('/nfse-infisc/lista', { params })
      setLista(r.data)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao carregar lista')
    } finally {
      setCarregando(false)
    }
  }, [ativos, empresaCod])

  useEffect(() => { carregar() }, [carregar])

  function pesquisar() { setAtivos({ ...filtros }) }
  function limpar()    { const p = filtroPadrao(); setFiltros(p); setAtivos(p) }

  function abrirCancel(row: NFSeRow) {
    setRowCancel(row)
    setMotivo('1')
    setErroCancel(null)
  }

  async function confirmarCancelamento() {
    if (!rowCancel) return
    setCancelando(true)
    setErroCancel(null)
    try {
      const r = await api.post(`/nfse-infisc/${rowCancel.numero}/cancelar?empcod=${rowCancel.empcod}`, {
        motivo,
      })
      if (r.data.cancelado) {
        setRowCancel(null)
        setMotivo('1')
        carregar()
      } else {
        setErroCancel(r.data.xMotivo || r.data.erro || 'Erro ao cancelar')
      }
    } catch (e: any) {
      setErroCancel(e?.response?.data?.error ?? 'Erro ao cancelar')
    } finally {
      setCancelando(false)
    }
  }

  function toggleMenuAcoes(e: React.MouseEvent, id: number) {
    if (acoesMenuId === id) { setAcoesMenuId(null); setAcoesMenuPos(null); return }
    const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect()
    setAcoesMenuId(id)
    setAcoesMenuPos({ top: rect.bottom + 2, left: rect.left })
  }
  function fecharMenuAcoes() { setAcoesMenuId(null); setAcoesMenuPos(null) }

  const menuItemStyle: React.CSSProperties = {
    display: 'block', width: '100%', textAlign: 'left', padding: '7px 12px',
    fontSize: '0.78rem', color: '#1f2937', background: 'none', border: 'none',
    cursor: 'pointer', whiteSpace: 'nowrap',
  }
  const onMenuHover = (e: React.MouseEvent<HTMLElement>) => (e.currentTarget.style.background = '#f3f4f6')
  const onMenuLeave = (e: React.MouseEvent<HTMLElement>) => (e.currentTarget.style.background = '')

  async function abrirEmail(row: NFSeRow) {
    setEmailModal(row); setEmailErro(null); setEmailMsg(null); setEmailCarregando(true)
    setEmailRemetenteNome(''); setEmailRemetenteEmail('')
    setEmailDestNome(''); setEmailDestCnpj(''); setEmailDestinatario('')
    setEmailCopias([]); setEmailAssunto(''); setEmailCorpo('')
    try {
      const r = await api.get(`/nfse-infisc/${row.numero}/email-preview?empcod=${row.empcod}`)
      const d = r.data
      setEmailRemetenteNome(d.remetenteNome ?? '')
      setEmailRemetenteEmail(d.remetenteEmail ?? '')
      setEmailDestNome(d.destinatarioNome ?? '')
      setEmailDestCnpj(d.destinatarioCnpj ?? '')
      setEmailDestinatario(d.destinatarioEmail ?? '')
      setEmailCopias(d.copias ?? [])
      setEmailAssunto(d.assunto ?? '')
      setEmailCorpo(d.corpo ?? '')
    } catch (e: any) {
      setEmailErro(e?.response?.data?.error || 'Erro ao carregar dados de e-mail')
    } finally {
      setEmailCarregando(false)
    }
  }

  function fecharEmail() { if (emailEnviando) return; setEmailModal(null) }

  function removerCopiaEmail(idx: number) {
    setEmailCopias(cs => cs.filter((_, i) => i !== idx))
  }

  async function enviarEmailNota() {
    if (!emailModal) return
    if (!emailDestinatario.trim()) { setEmailErro('Informe o e-mail do destinatário'); return }
    setEmailEnviando(true); setEmailErro(null)
    try {
      await api.post(`/nfse-infisc/${emailModal.numero}/enviar-email?empcod=${emailModal.empcod}`, {
        destinatario: emailDestinatario.trim(),
        copias: emailCopias.map(c => c.trim()).filter(Boolean),
        assunto: emailAssunto,
        corpo: emailCorpo,
      })
      setEmailMsg({ texto: 'E-mail enviado com sucesso!', ok: true })
      setEmailModal(null)
    } catch (e: any) {
      setEmailErro(e?.response?.data?.error || 'Erro ao enviar e-mail')
    } finally {
      setEmailEnviando(false)
    }
  }

  function copiarChave(chave: string, numero: number) {
    try {
      const el = document.createElement('textarea')
      el.value = chave
      el.style.cssText = 'position:fixed;opacity:0;top:0;left:0'
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      setChaveCopiadaId(numero)
      setTimeout(() => setChaveCopiadaId(null), 2000)
    } catch {}
  }

  function baixarXml(numero: number, empcod: number) {
    const url = `${api.defaults.baseURL}/nfse-infisc/${numero}/xml?empcod=${empcod}`
    const a = document.createElement('a')
    a.href = url
    a.download = `nfse-infisc-${numero}.xml`
    a.click()
  }

  function abrirDanfe(numero: number, empcod: number) {
    window.open(`${api.defaults.baseURL}/nfse-infisc/${numero}/danfe?empcod=${empcod}`, '_blank')
  }

  async function transmitir(row: NFSeRow) {
    setTransmitindoId(row.numero)
    setTransmitirMsg(null)
    try {
      const r = await api.post(`/nfse-infisc/${row.numero}/retransmitir?empcod=${row.empcod}`)
      const ok  = !!r.data.ok
      const msg = r.data.xMotivo || (ok ? 'Transmitida com sucesso!' : 'Falha na transmissão')
      setTransmitirMsg({ numero: row.numero, ok, msg })
      if (ok) carregar()
    } catch (e: any) {
      const msg = e?.response?.data?.error ?? 'Erro ao transmitir'
      setTransmitirMsg({ numero: row.numero, ok: false, msg })
    } finally {
      setTransmitindoId(null)
    }
  }

  function abrirConsulta(row: NFSeRow) {
    setConsultaRow(row)
    setConsultaFinanceiro([])
    setConsultaTributos([])
    api.get(`/nfse-infisc/${row.numero}/financeiro`, { params: { empcod: row.empcod } })
      .then(r => setConsultaFinanceiro(r.data?.parcelas ?? []))
      .catch(() => setConsultaFinanceiro([]))
    api.get(`/nfse-infisc/${row.numero}/tributos`, { params: { empcod: row.empcod } })
      .then(r => setConsultaTributos(r.data?.itens ?? []))
      .catch(() => setConsultaTributos([]))
  }

  function abrirFinanceiro(row: NFSeRow) {
    setFinRow(row)
    setFinQtdParcelas(1)
    setFinIntervalo(30)
    setFinContaCod('')
    setFinContaNome('')
    setFinContaResultados([])
    setFinContaAberto(false)
    setFinErro(null)
    setFinSucesso(false)
    const total = parseFloat(row.valor_total) || 0
    const hoje  = new Date()
    const v1    = new Date(hoje.getTime() + 30 * 86400000).toISOString().slice(0, 10)
    setFinParcelas([{ numero: 1, vencimento: v1, valor: total }])
  }

  function gerarParcelas(qtd: number, intervalo: number, total: number) {
    const hoje = new Date()
    return Array.from({ length: qtd }, (_, i) => {
      const d = new Date(hoje.getTime() + (i + 1) * intervalo * 86400000)
      return { numero: i + 1, vencimento: d.toISOString().slice(0, 10), valor: parseFloat((total / qtd).toFixed(2)) }
    })
  }

  async function buscarContas(q: string) {
    if (!q || q.length < 2) { setFinContaResultados([]); setFinContaAberto(false); return }
    try {
      const r = await api.get('/erp/contas', { params: { busca: q } })
      setFinContaResultados(r.data ?? [])
      setFinContaAberto((r.data ?? []).length > 0)
    } catch { setFinContaResultados([]) }
  }

  async function salvarFinanceiroLista() {
    if (!finRow) return
    if (!finContaCod.trim()) { setFinErro('Selecione a conta financeira'); return }
    setFinSalvando(true)
    setFinErro(null)
    try {
      const retencaoTotal = parseFloat(finRow.vl_iss) || 0
      await api.post(`/nfse-infisc/${finRow.numero}/financeiro?empcod=${finRow.empcod}`, {
        parcelas: finParcelas,
        contaCod: finContaCod,
        retencaoTotal,
      })
      setFinSucesso(true)
      setTimeout(() => setFinRow(null), 1500)
    } catch (e: any) {
      setFinErro(e?.response?.data?.error ?? 'Erro ao salvar financeiro')
    } finally {
      setFinSalvando(false)
    }
  }

  const sit = (row: NFSeRow) => SIT[row.sitnfe] ?? { label: `Sit ${row.sitnfe}`, color: '#6b7280', bg: '#f3f4f6' }

  return (
    <>
      {/* Barra de filtros */}
      <div className="card" style={{ padding: '0.65rem 1rem', marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, marginBottom: 2, color: '#374151' }}>Tomador (doc/nome)</div>
            <input style={inp} type="text" value={filtros.tomador} placeholder="CNPJ/CPF/nome"
              onChange={e => setFiltros(f => ({ ...f, tomador: e.target.value }))} />
          </div>
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, marginBottom: 2, color: '#374151' }}>Número</div>
            <input style={{ ...inp, width: 90 }} type="number" value={filtros.numero} placeholder="0"
              onChange={e => setFiltros(f => ({ ...f, numero: e.target.value }))} />
          </div>
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, marginBottom: 2, color: '#374151' }}>Competência de</div>
            <input style={{ ...inp, width: 135 }} type="date" value={filtros.dataInicio}
              onChange={e => setFiltros(f => ({ ...f, dataInicio: e.target.value }))} />
          </div>
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, marginBottom: 2, color: '#374151' }}>até</div>
            <input style={{ ...inp, width: 135 }} type="date" value={filtros.dataFim}
              onChange={e => setFiltros(f => ({ ...f, dataFim: e.target.value }))} />
          </div>
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, marginBottom: 2, color: '#374151' }}>Situação</div>
            <select style={inp} value={filtros.sitnfe} onChange={e => setFiltros(f => ({ ...f, sitnfe: e.target.value }))}>
              <option value="">Todas</option>
              <option value="5">Pendente</option>
              <option value="6">Autorizada</option>
              <option value="4">Rejeitada</option>
              <option value="7">Cancelada</option>
            </select>
          </div>
          <button className="btn btn-primary btn-sm" onClick={pesquisar} style={{ height: 30, alignSelf: 'flex-end' }}>Pesquisar</button>
          <button className="btn btn-outline btn-sm" onClick={limpar} style={{ height: 30, alignSelf: 'flex-end' }}>Limpar</button>
        </div>
      </div>

      {/* Tabela */}
      {emailMsg && (
        <div className={`alert ${emailMsg.ok ? 'alert-success' : 'alert-danger'}`} style={{ marginBottom: '0.75rem' }}>
          {emailMsg.texto}
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {carregando && (
          <div style={{ textAlign: 'center', padding: '2.5rem', color: '#6b7280' }}>Carregando...</div>
        )}
        {erro && (
          <div style={{ textAlign: 'center', padding: '2.5rem', color: '#dc2626' }}>{erro}</div>
        )}
        {!carregando && !erro && lista.length === 0 && (
          <div style={{ textAlign: 'center', padding: '2.5rem', color: '#9ca3af' }}>Nenhuma NFS-e INFISC encontrada para os filtros selecionados.</div>
        )}
        {!carregando && lista.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr className="thead-claro" style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  {['Nº/Série','Competência','Tomador','Razão Social','Serviço','Valor','ISS','Situação','Ações'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#374151', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lista.map(row => {
                  const s = sit(row)
                  return (
                    <tr key={row.numero} style={{ borderBottom: '1px solid #f3f4f6' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#dbeafe')}
                      onMouseLeave={e => (e.currentTarget.style.background = '')}>
                      <td style={{ padding: '8px 10px', fontWeight: 700, color: '#1a6ea8', whiteSpace: 'nowrap' }}>
                        <span style={{ cursor: 'pointer', textDecoration: 'underline dotted' }} onClick={() => abrirConsulta(row)}>
                          {row.numero}
                        </span>
                        <div style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 400 }}>Série {row.serie}</div>
                        <div style={{ fontSize: '0.68rem', color: '#9ca3af', fontWeight: 400 }}>{String(row.tp_amb) === '1' ? '✅ PROD' : '🔵 HOM'}</div>
                      </td>
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                        {fmtData(row.data_compet)}
                        {row.data_emissao && (
                          <div style={{ fontSize: '0.72rem', color: '#9ca3af' }}>Emi: {fmtData(row.data_emissao)}</div>
                        )}
                      </td>
                      <td style={{ padding: '8px 10px', maxWidth: 180 }}>
                        <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fmtDoc(row.cnpj_tomador)}</div>
                      </td>
                      <td style={{ padding: '8px 10px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span title={row.nome_tomador}>{row.nome_tomador || '—'}</span>
                      </td>
                      <td style={{ padding: '8px 10px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span title={row.desc_serv}>{row.desc_serv}</span>
                      </td>
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        R$ {fmtMoeda(row.valor_total)}
                      </td>
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        {Number(row.vl_iss) > 0 ? <>R$ {fmtMoeda(row.vl_iss)}</> : '—'}
                        {Number(row.pe_iss) > 0 && <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>{row.pe_iss}%</div>}
                      </td>
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                        <span style={{ background: s.bg, color: s.color, fontWeight: 700, fontSize: '0.72rem', borderRadius: 4, padding: '2px 8px' }}>
                          {s.label}
                        </span>
                        {row.xmotivo && (
                          <div style={{ fontSize: '0.68rem', color: '#6b7280', marginTop: 2, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.xmotivo}>
                            {row.xmotivo}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                        {/* Editar — só para pendente ou rejeitada */}
                        {(row.sitnfe === 4 || row.sitnfe === 5) && (
                          <button
                            title="Editar e retransmitir"
                            onClick={() => navigate(`/nfse-infisc/emitir?editId=${row.numero}&empcod=${row.empcod}`)}
                            style={{ background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, marginRight: 4 }}>
                            Editar
                          </button>
                        )}
                        {/* Transmitir — só para pendente ou rejeitada */}
                        {(row.sitnfe === 4 || row.sitnfe === 5) && (
                          <button
                            title="Transmitir para SEFAZ"
                            disabled={transmitindoId === row.numero}
                            onClick={() => transmitir(row)}
                            style={{ background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, opacity: transmitindoId === row.numero ? 0.6 : 1, marginRight: 4 }}>
                            {transmitindoId === row.numero ? '...' : 'Transmitir'}
                          </button>
                        )}
                        <button
                          onClick={e => toggleMenuAcoes(e, row.numero)}
                          style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>
                          Ações ▾
                        </button>
                        {acoesMenuId === row.numero && acoesMenuPos && (
                          <>
                            <div onClick={fecharMenuAcoes} style={{ position: 'fixed', inset: 0, zIndex: 99 }} />
                            <div style={{
                              position: 'fixed', top: acoesMenuPos.top, left: acoesMenuPos.left,
                              zIndex: 100, background: '#fff', border: '1px solid #e5e7eb',
                              borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                              minWidth: 190, overflow: 'hidden', padding: '4px 0',
                            }}>
                              <button style={menuItemStyle} onMouseEnter={onMenuHover} onMouseLeave={onMenuLeave}
                                onClick={() => { fecharMenuAcoes(); abrirDanfe(row.numero, row.empcod) }}>
                                DANFE (Visualizar)
                              </button>
                              <button style={menuItemStyle} onMouseEnter={onMenuHover} onMouseLeave={onMenuLeave}
                                onClick={() => { fecharMenuAcoes(); baixarXml(row.numero, row.empcod) }}>
                                Baixar XML
                              </button>
                              <button style={menuItemStyle} onMouseEnter={onMenuHover} onMouseLeave={onMenuLeave}
                                onClick={() => { fecharMenuAcoes(); abrirFinanceiro(row) }}>
                                Financeiro
                              </button>
                              <button style={menuItemStyle} onMouseEnter={onMenuHover} onMouseLeave={onMenuLeave}
                                onClick={() => { fecharMenuAcoes(); abrirEmail(row) }}>
                                Enviar por e-mail
                              </button>
                              <button style={menuItemStyle} onMouseEnter={onMenuHover} onMouseLeave={onMenuLeave}
                                onClick={() => { fecharMenuAcoes(); navigate(`/nfse-infisc/emitir?copyId=${row.numero}&empcod=${row.empcod}`) }}>
                                Copiar (nova nota)
                              </button>
                              {row.chave_acesso && (
                                <button style={menuItemStyle} onMouseEnter={onMenuHover} onMouseLeave={onMenuLeave}
                                  onClick={() => { fecharMenuAcoes(); copiarChave(row.chave_acesso, row.numero) }}>
                                  {chaveCopiadaId === row.numero ? '✓ Chave copiada' : 'Copiar Chave de Acesso'}
                                </button>
                              )}
                              {row.sitnfe === 6 && (
                                <button style={{ ...menuItemStyle, color: '#dc2626', borderTop: '1px solid #f3f4f6' }} onMouseEnter={onMenuHover} onMouseLeave={onMenuLeave}
                                  onClick={() => { fecharMenuAcoes(); abrirCancel(row) }}>
                                  Cancelar NFS-e
                                </button>
                              )}
                            </div>
                          </>
                        )}
                        {/* Mensagem transmissão */}
                        {transmitirMsg?.numero === row.numero && (
                          <div style={{ fontSize: '0.7rem', color: transmitirMsg.ok ? '#15803d' : '#dc2626', marginTop: 2 }}>
                            {transmitirMsg.msg}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de envio de e-mail */}
      {emailModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={fecharEmail}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '1.5rem',
            width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: 700, color: '#1d4ed8' }}>
              Enviar XML/DANFE por E-mail
            </h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: '#6b7280' }}>
              Nº {emailModal.numero} — {fmtDoc(emailModal.cnpj_tomador)}
            </p>

            {emailCarregando ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>Carregando...</div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem 1rem', marginBottom: '0.75rem' }}>
                  {kv('Remetente', emailRemetenteNome || '—')}
                  {kv('E-mail Remetente', emailRemetenteEmail || '—')}
                </div>

                {kv('Destinatário', `${emailDestNome}${emailDestCnpj ? ' — ' + fmtDoc(emailDestCnpj) : ''}`)}

                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', display: 'block', margin: '0.6rem 0 4px' }}>
                  E-mail do Destinatário
                </label>
                <input value={emailDestinatario} onChange={e => setEmailDestinatario(e.target.value)}
                  placeholder="email@exemplo.com"
                  style={{
                    width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db',
                    borderRadius: 4, padding: '6px 8px', fontSize: '0.85rem',
                  }} />

                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', display: 'block', margin: '0.75rem 0 4px' }}>
                  Cópias
                </label>
                {emailCopias.length === 0 && (
                  <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginBottom: 4 }}>Nenhuma cópia cadastrada</div>
                )}
                {emailCopias.map((c, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                    <input value={c}
                      onChange={e => setEmailCopias(cs => cs.map((v, i) => i === idx ? e.target.value : v))}
                      style={{ flex: 1, border: '1px solid #d1d5db', borderRadius: 4, padding: '5px 8px', fontSize: '0.82rem' }} />
                    <button onClick={() => removerCopiaEmail(idx)}
                      title="Remover desta cópia (não altera o cadastro do cliente)"
                      style={{
                        background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5',
                        borderRadius: 4, padding: '0 10px', cursor: 'pointer',
                      }}>
                      ✕
                    </button>
                  </div>
                ))}

                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', display: 'block', margin: '0.75rem 0 4px' }}>
                  Assunto
                </label>
                <input value={emailAssunto} onChange={e => setEmailAssunto(e.target.value)}
                  style={{
                    width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db',
                    borderRadius: 4, padding: '6px 8px', fontSize: '0.85rem',
                  }} />

                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', display: 'block', margin: '0.75rem 0 4px' }}>
                  Mensagem
                </label>
                <textarea value={emailCorpo} onChange={e => setEmailCorpo(e.target.value)} rows={5}
                  style={{
                    width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db',
                    borderRadius: 4, padding: '6px 8px', fontSize: '0.85rem', resize: 'vertical',
                  }} />

                {emailErro && <div style={{ color: '#dc2626', fontSize: '0.78rem', marginTop: 8 }}>{emailErro}</div>}

                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                  <button className="btn btn-sm" onClick={() => setCadEmailsAberto(true)} disabled={emailEnviando || !emailDestCnpj}
                    title="Cadastrar/alterar os e-mails deste cliente sem sair da tela"
                    style={{ marginRight: 'auto', color: '#1b3f7b', border: '1px solid #c7d6ea', background: '#eef3f9', fontWeight: 600 }}>
                    ✉ Cadastrar e-mails
                  </button>
                  {cadEmailsAberto && (
                    <CadastroEmailsClienteModal doc={emailDestCnpj} nome={emailDestNome} tipo="M"
                      onClose={() => setCadEmailsAberto(false)}
                      onSalvo={(principal, copias) => { setEmailDestinatario(principal); setEmailCopias(copias); setCadEmailsAberto(false) }} />
                  )}
                  <button className="btn btn-sm" onClick={fecharEmail} disabled={emailEnviando} style={{ color: '#6b7280' }}>
                    Voltar
                  </button>
                  <button onClick={enviarEmailNota}
                    disabled={emailEnviando || !emailDestinatario.trim()}
                    style={{
                      background: '#1d4ed8', color: '#fff', border: 'none',
                      borderRadius: 4, padding: '5px 16px', fontSize: '0.82rem',
                      fontWeight: 600, cursor: emailEnviando ? 'not-allowed' : 'pointer',
                      opacity: emailEnviando || !emailDestinatario.trim() ? 0.6 : 1,
                    }}>
                    {emailEnviando ? 'Enviando...' : 'Enviar E-mail'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal Cancelamento */}
      {rowCancel && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget && !cancelando) { setRowCancel(null) } }}>
          <div style={{ background: '#fff', borderRadius: '0.75rem', padding: '1.5rem', width: 460, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ fontWeight: 800, fontSize: '1.1rem', marginBottom: 12, color: '#dc2626' }}>Cancelar NFS-e INFISC</div>
            <div style={{ fontSize: '0.82rem', color: '#374151', marginBottom: 12 }}>
              <b>NFS-e {rowCancel.numero}</b> · Série {rowCancel.serie}<br />
              Tomador: {fmtDoc(rowCancel.cnpj_tomador)}<br />
              Valor: R$ {fmtMoeda(rowCancel.valor_total)}
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: '0.78rem', fontWeight: 700, display: 'block', marginBottom: 4 }}>Motivo do cancelamento *</label>
              <select value={motivo} onChange={e => setMotivo(e.target.value)}
                style={{ width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 4, padding: '6px 10px', fontSize: '0.85rem' }}>
                <option value="1">1 — Erro na emissão</option>
                <option value="2">2 — Serviço não prestado</option>
                <option value="3">3 — Duplicidade de nota</option>
                <option value="4">4 — Outros</option>
              </select>
            </div>
            {erroCancel && (
              <div style={{ color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, padding: '6px 10px', fontSize: '0.82rem', marginBottom: 10 }}>
                {erroCancel}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-outline btn-sm" onClick={() => setRowCancel(null)} disabled={cancelando}>Fechar</button>
              <button className="btn btn-danger btn-sm" onClick={confirmarCancelamento} disabled={cancelando}>
                {cancelando ? 'Cancelando...' : 'Confirmar Cancelamento'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Financeiro */}
      {finRow && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget && !finSalvando) setFinRow(null) }}>
          <div style={{ background: '#fff', borderRadius: '0.75rem', padding: '1.5rem', width: 520, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ fontWeight: 800, fontSize: '1.1rem', marginBottom: 4, color: '#1a6ea8' }}>Financeiro — NFS-e {finRow.numero}</div>
            <div style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: 16 }}>
              {fmtDoc(finRow.cnpj_tomador)} · R$ {fmtMoeda(finRow.valor_total)}
            </div>

            {finSucesso ? (
              <div style={{ textAlign: 'center', color: '#15803d', padding: '1.5rem', fontSize: '1rem', fontWeight: 700 }}>
                Financeiro salvo com sucesso!
              </div>
            ) : (
              <>
                {/* Conta */}
                <div style={{ marginBottom: 14, position: 'relative' }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 700, display: 'block', marginBottom: 4 }}>Conta Financeira *</label>
                  <input
                    type="text"
                    value={finContaNome || finContaCod}
                    placeholder="Digite para buscar conta..."
                    onChange={e => { setFinContaNome(e.target.value); setFinContaCod(''); buscarContas(e.target.value) }}
                    style={{ width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 4, padding: '6px 10px', fontSize: '0.85rem', boxSizing: 'border-box' as const }}
                  />
                  {finContaAberto && finContaResultados.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 50, maxHeight: 180, overflowY: 'auto' }}>
                      {finContaResultados.map((c: any) => (
                        <div key={c.dep_cod}
                          onClick={() => { setFinContaCod(c.dep_cod.trim()); setFinContaNome(c.dep_des ?? ''); setFinContaAberto(false); setFinContaResultados([]) }}
                          style={{ padding: '6px 12px', cursor: 'pointer', fontSize: '0.82rem', borderBottom: '1px solid #f3f4f6' }}
                          onMouseEnter={e => (e.currentTarget.style.background = '#f0f9ff')}
                          onMouseLeave={e => (e.currentTarget.style.background = '')}>
                          <b>{c.dep_cod?.trim()}</b> — {c.dep_des}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Parcelas */}
                <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.78rem', fontWeight: 700, display: 'block', marginBottom: 4 }}>Qtd. Parcelas</label>
                    <input type="number" min={1} max={60} value={finQtdParcelas}
                      onChange={e => {
                        const qtd = Math.max(1, Number(e.target.value))
                        setFinQtdParcelas(qtd)
                        setFinParcelas(gerarParcelas(qtd, finIntervalo, parseFloat(finRow!.valor_total) || 0))
                      }}
                      style={{ width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 4, padding: '6px 10px', fontSize: '0.85rem' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.78rem', fontWeight: 700, display: 'block', marginBottom: 4 }}>Intervalo (dias)</label>
                    <input type="number" min={1} value={finIntervalo}
                      onChange={e => {
                        const iv = Math.max(1, Number(e.target.value))
                        setFinIntervalo(iv)
                        setFinParcelas(gerarParcelas(finQtdParcelas, iv, parseFloat(finRow!.valor_total) || 0))
                      }}
                      style={{ width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 4, padding: '6px 10px', fontSize: '0.85rem' }} />
                  </div>
                </div>

                {/* Tabela de parcelas */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 700, display: 'block', marginBottom: 6 }}>Parcelas</label>
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                    {finParcelas.map((p, i) => (
                      <div key={p.numero} style={{ display: 'flex', gap: 8, padding: '6px 10px', borderBottom: i < finParcelas.length - 1 ? '1px solid #f3f4f6' : undefined, alignItems: 'center' }}>
                        <span style={{ fontSize: '0.78rem', color: '#9ca3af', minWidth: 24 }}>{p.numero}</span>
                        <input type="date" value={p.vencimento}
                          onChange={e => { const ps = [...finParcelas]; ps[i] = { ...ps[i], vencimento: e.target.value }; setFinParcelas(ps) }}
                          style={{ border: '1px solid #e5e7eb', borderRadius: 4, padding: '3px 6px', fontSize: '0.8rem', flex: 1 }} />
                        <input type="number" step="0.01" value={p.valor}
                          onChange={e => { const ps = [...finParcelas]; ps[i] = { ...ps[i], valor: parseFloat(e.target.value) || 0 }; setFinParcelas(ps) }}
                          style={{ border: '1px solid #e5e7eb', borderRadius: 4, padding: '3px 6px', fontSize: '0.8rem', width: 100, textAlign: 'right' as const }} />
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 4, textAlign: 'right' as const }}>
                    Total parcelas: R$ {fmtMoeda(finParcelas.reduce((s, p) => s + p.valor, 0))}
                  </div>
                </div>

                {finErro && (
                  <div style={{ color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, padding: '6px 10px', fontSize: '0.82rem', marginBottom: 10 }}>
                    {finErro}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button className="btn btn-outline btn-sm" onClick={() => setFinRow(null)} disabled={finSalvando}>Fechar</button>
                  <button className="btn btn-primary btn-sm" onClick={salvarFinanceiroLista} disabled={finSalvando}>
                    {finSalvando ? 'Salvando...' : 'Salvar Financeiro'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal Consulta */}
      {consultaRow && (() => {
        const s   = sit(consultaRow)
        const xml = (consultaRow.xmlpro || '').trim() || consultaRow.xmlnfe || ''
        const det = parseNfse(xml)
        const prestador = det?.prestador ?? pessoaVazia
        const tomador   = det?.tomador   ?? pessoaVazia
        const nNFSe     = det?.numero || (Number(consultaRow.nnfse) > 0 ? consultaRow.nnfse : '')
        const isProd    = String(consultaRow.tp_amb) === '1'

        // Base/alíquota/CST/retenção de verdade vêm da ntvits (mesma tabela que o ERP mostra em
        // "Complemento/Tributação"); se não achar linha lá, cai pros totais da própria ntvcad.
        const itTrib = consultaTributos[0]
        const tributos = itTrib ? [
          { n: 'ICMS',   cst: itTrib.icms_cst, base: itTrib.icms_base,  aliq: itTrib.icms_aliq,  valor: itTrib.icms_dest,  ret: null              },
          { n: 'ISSQN',  cst: null,            base: itTrib.issqn_base, aliq: itTrib.issqn_aliq, valor: itTrib.issqn_dest, ret: itTrib.issqn_ret  },
          { n: 'INSS',   cst: null,            base: itTrib.inss_base,  aliq: itTrib.inss_aliq,  valor: itTrib.inss_dest,  ret: itTrib.inss_ret   },
          { n: 'IRRF',   cst: null,            base: itTrib.irrf_base,  aliq: itTrib.irrf_aliq,  valor: itTrib.irrf_dest,  ret: itTrib.irrf_ret   },
          { n: 'PIS',    cst: null,            base: itTrib.pis_base,   aliq: itTrib.pis_aliq,   valor: itTrib.pis_dest,   ret: itTrib.pis_ret    },
          { n: 'COFINS', cst: null,            base: itTrib.cofins_base,aliq: itTrib.cofins_aliq,valor: itTrib.cofins_dest,ret: itTrib.cofins_ret },
          { n: 'CSLL',   cst: null,            base: itTrib.csll_base,  aliq: itTrib.csll_aliq,  valor: itTrib.csll_dest,  ret: itTrib.csll_ret   },
          { n: 'IPI',    cst: null,            base: itTrib.ipi_base,   aliq: itTrib.ipi_aliq,   valor: itTrib.ipi_dest,   ret: null              },
        ].filter(t => Number(t.valor) !== 0) : [
          { n: 'ISS',    cst: null, base: null, aliq: consultaRow.pe_iss, valor: consultaRow.vl_iss, ret: null },
          { n: 'INSS',   cst: null, base: null, aliq: '', valor: consultaRow.vl_inss, ret: null },
          { n: 'IR',     cst: null, base: null, aliq: '', valor: consultaRow.vl_ir,   ret: null },
          { n: 'COFINS', cst: null, base: null, aliq: '', valor: consultaRow.vl_cof,  ret: null },
          { n: 'PIS',    cst: null, base: null, aliq: '', valor: consultaRow.vl_pis,  ret: null },
          { n: 'CSLL',   cst: null, base: null, aliq: '', valor: consultaRow.vl_csll, ret: null },
        ].filter(t => Number(t.valor) > 0)

        const ibsLinhas = itTrib ? [
          { n: 'CBS',                                bc: itTrib.cbs_bce,   aliRef: itTrib.cbs_are,   red: itTrib.cbs_ral,   aliEf: itTrib.cbs_aef,   valor: itTrib.cbs_val   },
          { n: `IBS UF (${itTrib.ibsuf_est || '?'})`, bc: itTrib.ibsuf_bce, aliRef: itTrib.ibsuf_are, red: itTrib.ibsuf_ral, aliEf: itTrib.ibsuf_aef, valor: itTrib.ibsuf_val },
          { n: 'IBS Municipal',                       bc: itTrib.mun_bce,  aliRef: itTrib.mun_are,   red: itTrib.mun_ral,   aliEf: itTrib.mun_aef,   valor: itTrib.mun_val   },
        ].filter(l => Number(l.valor) !== 0) : []

        const secTitle = (t: string) => (
          <div style={{ fontSize: '0.7rem', fontWeight: 800, color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: '0.05em', borderBottom: '1px solid #e5e7eb', paddingBottom: 4, marginBottom: 10, marginTop: 16 }}>{t}</div>
        )
        const row2 = (children: React.ReactNode) => (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem 1.2rem', marginBottom: 4 }}>{children}</div>
        )
        const row3 = (children: React.ReactNode) => (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.6rem 1.2rem', marginBottom: 4 }}>{children}</div>
        )

        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={e => { if (e.target === e.currentTarget) setConsultaRow(null) }}>
            <div style={{ background: '#fff', borderRadius: '0.75rem', padding: '1.5rem', width: 760, maxWidth: '97vw', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>

              {/* Cabeçalho */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '1.15rem', color: '#1a6ea8' }}>
                    NFS-e INFISC{nNFSe ? ` Nº ${nNFSe}` : ''} — Seq. {consultaRow.numero}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 2 }}>
                    Série {consultaRow.serie} &nbsp;·&nbsp;
                    Competência: {fmtData(consultaRow.data_compet)} &nbsp;·&nbsp;
                    Emissão: {fmtData(consultaRow.data_emissao)} &nbsp;·&nbsp;
                    <span style={{ color: isProd ? '#15803d' : '#2563eb', fontWeight: 700 }}>{isProd ? '✅ PROD' : '🔵 HOM'}</span>
                  </div>
                </div>
                <span style={{ background: s.bg, color: s.color, fontWeight: 700, fontSize: '0.78rem', borderRadius: 4, padding: '3px 10px', whiteSpace: 'nowrap' as const }}>{s.label}</span>
              </div>

              {/* Chave */}
              {consultaRow.chave_acesso && (
                <div style={{ background: '#f1f5f9', borderRadius: 4, padding: '5px 10px', fontSize: '0.72rem', fontFamily: 'monospace', wordBreak: 'break-all' as const, color: '#0d2d4e', marginTop: 8, marginBottom: 4 }}>
                  {consultaRow.chave_acesso.trim()}
                  <button onClick={() => copiarChave(consultaRow.chave_acesso, consultaRow.numero)}
                    style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', fontSize: '0.72rem' }}>
                    {chaveCopiadaId === consultaRow.numero ? '✓ Copiado' : 'Copiar'}
                  </button>
                </div>
              )}

              {/* Natureza da operação */}
              {secTitle('Natureza da Operação')}
              {row3(<>
                {kv('Código da Natureza', consultaRow.natureza_cod || '—')}
                <div style={{ gridColumn: 'span 2' }}>{kv('Descrição / Detalhes da Natureza', consultaRow.natureza_det || '—')}</div>
              </>)}

              {/* Prestador */}
              {secTitle('Prestador')}
              {row3(<>
                {kv('Razão Social', prestador.nome || '—')}
                {kv('CNPJ', fmtDoc(prestador.doc) || '—')}
                {kv('Inscrição Municipal', prestador.im || '—')}
              </>)}
              {(prestador.endereco || prestador.fone || prestador.email) && row3(<>
                {prestador.endereco && kv('Endereço', prestador.endereco)}
                {prestador.fone && kv('Telefone', prestador.fone)}
                {prestador.email && kv('E-mail', prestador.email)}
              </>)}

              {/* Tomador */}
              {secTitle('Tomador')}
              {row3(<>
                {kv('Razão Social / Nome', tomador.nome || '—')}
                {kv('CNPJ / CPF', fmtDoc(tomador.doc || consultaRow.cnpj_tomador) || '—')}
                {kv('Inscrição Municipal', tomador.im || '—')}
              </>)}
              {(tomador.endereco || tomador.fone || tomador.email) && row3(<>
                {tomador.endereco && kv('Endereço', tomador.endereco)}
                {tomador.fone && kv('Telefone', tomador.fone)}
                {tomador.email && kv('E-mail', tomador.email)}
              </>)}

              {/* Serviço */}
              {secTitle('Serviço')}
              {row2(<>
                {kv('Descrição', det?.xServ || consultaRow.desc_serv || '—')}
                {det?.cServ && kv('Código do Serviço', det.cServ)}
              </>)}
              {consultaRow.numero_viagem && row3(<>
                {kvViagem(consultaRow.numero_viagem)}
              </>)}
              {(det?.qTrib || det?.vUnit || det?.localPrestacao) && row3(<>
                {det?.qTrib && kv('Quantidade', `${det.qTrib}${det.uTrib ? ` ${det.uTrib}` : ''}`)}
                {det?.vUnit && kv('Valor Unitário', `R$ ${fmtMoeda(det.vUnit)}`)}
                {det?.localPrestacao && kv('Local de Prestação (IBGE)', det.localPrestacao)}
              </>)}

              {/* Valores e Impostos */}
              {secTitle('Valores e Impostos')}
              <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', marginBottom: tributos.length ? 10 : 0 }}>
                <div>
                  <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' as const, marginBottom: 1 }}>Valor do Serviço</div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#15803d' }}>R$ {fmtMoeda(consultaRow.valor_total)}</div>
                </div>
                {!itTrib && det?.baseCalculo && kv('Base de Cálculo ISS', `R$ ${fmtMoeda(det.baseCalculo)}`)}
              </div>
              {tributos.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.79rem', marginBottom: ibsLinhas.length ? 10 : 0 }}>
                  <thead>
                    <tr className="thead-claro" style={{ background: '#f9fafb' }}>
                      {['Tributo', 'CST', 'Base de Cálculo', 'Alíquota', 'Valor', 'Retenção'].map(h => (
                        <th key={h} style={{ padding: '3px 6px', border: '1px solid #e5e7eb', color: '#9ca3af', fontWeight: 600, textAlign: h === 'Tributo' || h === 'CST' ? 'left' : 'right' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tributos.map((t, i) => (
                      <tr key={i}>
                        <td style={{ padding: '3px 6px', border: '1px solid #e5e7eb', fontWeight: 700 }}>{t.n}</td>
                        <td style={{ padding: '3px 6px', border: '1px solid #e5e7eb', fontFamily: 'monospace' }}>{t.cst ?? '—'}</td>
                        <td style={{ padding: '3px 6px', border: '1px solid #e5e7eb', textAlign: 'right' }}>{Number(t.base) ? `R$ ${fmtMoeda(t.base)}` : '—'}</td>
                        <td style={{ padding: '3px 6px', border: '1px solid #e5e7eb', textAlign: 'right' }}>{Number(t.aliq) ? `${Number(t.aliq).toFixed(2)}%` : '—'}</td>
                        <td style={{ padding: '3px 6px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 600 }}>R$ {fmtMoeda(t.valor)}</td>
                        <td style={{ padding: '3px 6px', border: '1px solid #e5e7eb', textAlign: 'right', color: '#6b7280' }}>{Number(t.ret) ? `R$ ${fmtMoeda(t.ret)}` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {itTrib && (cstDes(itTrib.ibs_cst) || classTribDes(itTrib.ibs_cst, itTrib.ibs_classtrib)) && (
                <div style={{ marginBottom: ibsLinhas.length ? 10 : 0, fontSize: '0.78rem', color: '#374151', lineHeight: 1.4 }}>
                  {cstDes(itTrib.ibs_cst) && <div><strong>CST {itTrib.ibs_cst}:</strong> {cstDes(itTrib.ibs_cst)}</div>}
                  {classTribDes(itTrib.ibs_cst, itTrib.ibs_classtrib) && (
                    <div><strong>cClassTrib {itTrib.ibs_classtrib}:</strong> {classTribDes(itTrib.ibs_cst, itTrib.ibs_classtrib)}</div>
                  )}
                </div>
              )}
              {ibsLinhas.length > 0 && (
                <div>
                  <div style={{ fontSize: '0.64rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' as const, marginBottom: 3 }}>IBS / CBS</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.79rem' }}>
                    <thead>
                      <tr className="thead-claro" style={{ background: '#f0fdf4' }}>
                        {['Componente', 'Base Ef.', 'Alíq. Ref.', 'Red. (%)', 'Alíq. Ef.', 'Valor'].map(h => (
                          <th key={h} style={{ padding: '3px 6px', border: '1px solid #d1fae5', color: '#9ca3af', fontWeight: 600, textAlign: h === 'Componente' ? 'left' : 'right' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {ibsLinhas.map((l, i) => (
                        <tr key={i}>
                          <td style={{ padding: '3px 6px', border: '1px solid #d1fae5', fontWeight: 700 }}>{l.n}</td>
                          <td style={{ padding: '3px 6px', border: '1px solid #d1fae5', textAlign: 'right' }}>{Number(l.bc) ? `R$ ${fmtMoeda(l.bc)}` : '—'}</td>
                          <td style={{ padding: '3px 6px', border: '1px solid #d1fae5', textAlign: 'right' }}>{Number(l.aliRef) ? `${Number(l.aliRef).toFixed(4)}%` : '—'}</td>
                          <td style={{ padding: '3px 6px', border: '1px solid #d1fae5', textAlign: 'right' }}>{Number(l.red) ? `${Number(l.red).toFixed(2)}%` : '—'}</td>
                          <td style={{ padding: '3px 6px', border: '1px solid #d1fae5', textAlign: 'right' }}>{Number(l.aliEf) ? `${Number(l.aliEf).toFixed(4)}%` : '—'}</td>
                          <td style={{ padding: '3px 6px', border: '1px solid #d1fae5', textAlign: 'right', fontWeight: 700, color: '#15803d' }}>R$ {fmtMoeda(l.valor)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Situação */}
              {secTitle('Situação SEFAZ')}
              {row3(<>
                {kv('Situação', s.label)}
                {consultaRow.cstat && kv('cStat', consultaRow.cstat)}
                {det?.codVerificacao && kv('Código de Verificação', det.codVerificacao)}
              </>)}
              {consultaRow.xmotivo && kv('Observação', <span style={{ whiteSpace: 'pre-line' as const }}>{consultaRow.xmotivo}</span>)}

              {/* Financeiro */}
              {secTitle('Financeiro')}
              {consultaFinanceiro.length === 0 ? (
                <span style={{ fontSize: '0.82rem', color: '#9ca3af' }}>Nenhum financeiro lançado para esta nota.</span>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.79rem' }}>
                  <thead>
                    <tr className="thead-claro" style={{ background: '#f0fdf4' }}>
                      {['Nº', 'Vencimento', 'Valor', 'Saldo', 'Conta'].map(h => (
                        <th key={h} style={{ padding: '3px 6px', border: '1px solid #bbf7d0', color: '#065f46', fontWeight: 700, textAlign: h === 'Nº' ? 'center' : 'left' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {consultaFinanceiro.map((p, i) => (
                      <tr key={i}>
                        <td style={{ padding: '3px 6px', border: '1px solid #bbf7d0', textAlign: 'center' }}>{p.numero}</td>
                        <td style={{ padding: '3px 6px', border: '1px solid #bbf7d0' }}>{fmtData(p.vencimento)}</td>
                        <td style={{ padding: '3px 6px', border: '1px solid #bbf7d0', textAlign: 'right' }}>R$ {fmtMoeda(p.valor)}</td>
                        <td style={{ padding: '3px 6px', border: '1px solid #bbf7d0', textAlign: 'right' }}>R$ {fmtMoeda(p.saldo)}</td>
                        <td style={{ padding: '3px 6px', border: '1px solid #bbf7d0', fontFamily: 'monospace' }}>{p.contaCod}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Ações */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end', marginTop: 20, borderTop: '1px solid #e5e7eb', paddingTop: 14 }}>
                <button className="btn btn-outline btn-sm" onClick={() => setConsultaRow(null)}>Fechar</button>
                <button className="btn btn-outline btn-sm" onClick={() => abrirDanfe(consultaRow.numero, consultaRow.empcod)}>DANFE</button>
                <button className="btn btn-outline btn-sm" onClick={() => baixarXml(consultaRow.numero, consultaRow.empcod)}>Baixar XML</button>
                {(consultaRow.sitnfe === 4 || consultaRow.sitnfe === 5) && (
                  <button className="btn btn-outline btn-sm" style={{ color: '#15803d', borderColor: '#86efac' }}
                    onClick={() => { setConsultaRow(null); transmitir(consultaRow) }}>Transmitir</button>
                )}
                {consultaRow.sitnfe === 6 && (
                  <button className="btn btn-danger btn-sm" onClick={() => { setConsultaRow(null); abrirCancel(consultaRow) }}>Cancelar</button>
                )}
              </div>
            </div>
          </div>
        )
      })()}
    </>
  )
}
